import type { Severity } from '@/types';

/**
 * Severity tokens — single source of truth for app, portal, and PDF.
 *
 * Two client-facing severities plus a neutral observation bucket for null.
 */
export type SeverityKey = 'safety_concern' | 'recommendation' | 'observation';

export interface SeverityToken {
  key: SeverityKey;
  label: string;        // inspector-facing + client-facing are the same
  classes: {
    dot: string;
    pill: string;
    ring: string;
    text: string;
  };
}

export const SEVERITY: Record<SeverityKey, SeverityToken> = {
  safety_concern: {
    key: 'safety_concern',
    label: 'Safety Concern',
    classes: {
      dot:  'bg-sev-critical',
      pill: 'bg-sev-critical-soft text-sev-critical-ink',
      ring: 'ring-1 ring-sev-critical/20',
      text: 'text-sev-critical',
    },
  },
  recommendation: {
    key: 'recommendation',
    label: 'Recommendation',
    classes: {
      dot:  'bg-sev-minor',
      pill: 'bg-sev-minor-soft text-sev-minor-ink',
      ring: 'ring-1 ring-sev-minor/20',
      text: 'text-sev-minor',
    },
  },
  observation: {
    key: 'observation',
    label: 'Observation',
    classes: {
      dot:  'bg-ink/20',
      pill: 'bg-surface-2 text-ink-2',
      ring: 'ring-1 ring-ink/10',
      text: 'text-ink-2',
    },
  },
};

export function severityKey(s: Severity): SeverityKey {
  return s === null ? 'observation' : s;
}

/**
 * Hex values for Puppeteer / PDF pipeline — mirrors the CSS custom properties
 * so the server can render identical severity colors without a browser.
 */
export const SEVERITY_HEX: Record<SeverityKey, { solid: string; soft: string; ink: string }> = {
  safety_concern: { solid: '#9A2C1F', soft: '#FBECEA', ink: '#5E1A10' },
  recommendation: { solid: '#935E0A', soft: '#FBF3E2', ink: '#583604' },
  observation:    { solid: '#6B7280', soft: '#F3F4F6', ink: '#374151' },
};
